import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../utils";
import { Button } from "../button";
import { Input } from "./Input";
import { Textarea } from "../textarea";

function InputGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="input-group"
      role="group"
      className={cn(
        "al-group/input-group al-border-input al-bg-background al-relative al-flex al-w-full al-items-center al-overflow-hidden al-rounded-md al-border al-outline-none al-transition-[color,box-shadow]",
        "al-h-10 has-[>textarea]:al-h-auto",

        // Variants based on alignment.
        "has-[>[data-align=inline-start]]:[&>input]:al-pl-2",
        "has-[>[data-align=inline-end]]:[&>input]:al-pr-2",
        "has-[>[data-align=block-start]]:al-h-auto has-[>[data-align=block-start]]:al-flex-col has-[>[data-align=block-start]]:[&>input]:al-pb-3",
        "has-[>[data-align=block-end]]:al-h-auto has-[>[data-align=block-end]]:al-flex-col has-[>[data-align=block-end]]:[&>input]:al-pt-3",

        // Focus state - matches Input component styles.
        "has-[[data-slot=input-group-control]:focus-visible]:al-ring-ring has-[[data-slot=input-group-control]:focus-visible]:al-ring-2 has-[[data-slot=input-group-control]:focus-visible]:al-ring-offset-2 has-[[data-slot=input-group-control]:focus-visible]:al-ring-offset-background",

        // Error state.
        "has-[[data-slot][aria-invalid=true]]:al-ring-destructive/20 has-[[data-slot][aria-invalid=true]]:al-border-destructive dark:has-[[data-slot][aria-invalid=true]]:al-ring-destructive/40",

        className
      )}
      {...props}
    />
  );
}

const inputGroupAddonVariants = cva(
  "al-text-muted-foreground al-flex al-cursor-text al-select-none al-items-center al-gap-2 al-text-sm al-font-medium al-group-data-[disabled=true]/input-group:opacity-50 [&>kbd]:al-rounded-[calc(var(--radius)-5px)] [&>svg:not([class*=size-])]:al-size-4",
  {
    variants: {
      align: {
        "inline-start":
          "al-order-first al-pl-3 al-pr-1 has-[>button]:al-ml-[-0.45rem] has-[>kbd]:al-ml-[-0.35rem]",
        "inline-end":
          "al-order-last al-pl-1 al-pr-3 has-[>button]:al-mr-[-0.4rem] has-[>kbd]:al-mr-[-0.35rem]",
        "block-start":
          "[.border-b]:al-pb-3 al-order-first al-w-full al-justify-start al-px-3 al-pt-3 al-group-has-[>input]/input-group:pt-2.5",
        "block-end":
          "[.border-t]:al-pt-3 al-order-last al-w-full al-justify-start al-px-3 al-pb-3 al-group-has-[>input]/input-group:pb-2.5",
      },
    },
    defaultVariants: {
      align: "inline-start",
    },
  }
);

function InputGroupAddon({
  className,
  align = "inline-start",
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof inputGroupAddonVariants>) {
  return (
    <div
      role="group"
      data-slot="input-group-addon"
      data-align={align}
      className={cn(inputGroupAddonVariants({ align }), className)}
      onClick={e => {
        if ((e.target as HTMLElement).closest("button")) {
          return;
        }
        e.currentTarget.parentElement?.querySelector("input")?.focus();
      }}
      {...props}
    />
  );
}

const inputGroupButtonVariants = cva("al-flex al-items-center al-gap-2 al-text-sm al-shadow-none", {
  variants: {
    size: {
      xs: "al-h-6 al-gap-1 al-rounded-[calc(var(--radius)-5px)] al-px-2 has-[>svg]:al-px-2 [&>svg:not([class*=size-])]:al-size-3.5",
      sm: "al-h-8 al-gap-1.5 al-rounded-md al-px-2.5 has-[>svg]:al-px-2.5",
      icon: "!al-size-7 al-rounded-[calc(var(--radius)-5px)] al-p-0 has-[>svg]:al-p-0",
    },
  },
  defaultVariants: {
    size: "xs",
  },
});

function InputGroupButton({
  className,
  type = "button",
  variant = "ghost",
  size = "xs",
  ...props
}: Omit<React.ComponentProps<typeof Button>, "size"> &
  VariantProps<typeof inputGroupButtonVariants>) {
  return (
    <Button
      type={type}
      data-size={size}
      variant={variant}
      className={cn(inputGroupButtonVariants({ size }), className)}
      {...props}
    />
  );
}

function InputGroupText({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      className={cn(
        "al-text-muted-foreground al-flex al-items-center al-gap-2 al-text-sm [&_svg:not([class*=size-])]:al-size-4 [&_svg]:al-pointer-events-none",
        className
      )}
      {...props}
    />
  );
}

function InputGroupInput({ className, size: _size, ...props }: React.ComponentProps<"input">) {
  return (
    <Input
      data-slot="input-group-control"
      className={cn(
        "al-flex-1 !al-h-full al-py-0 al-rounded-none al-border-0 al-bg-transparent al-shadow-none focus-visible:!al-ring-offset-0 focus-visible:!al-ring-0 dark:al-bg-transparent",
        className
      )}
      {...props}
    />
  );
}

function InputGroupTextarea({ className, ...props }: React.ComponentProps<"textarea">) {
  return (
    <Textarea
      data-slot="input-group-control"
      className={cn(
        "al-flex-1 al-resize-none al-rounded-none al-border-0 al-bg-transparent al-py-3 al-shadow-none focus-visible:al-ring-0 focus-visible:!al-ring-offset-0 focus-visible:!al-ring-0  dark:al-bg-transparent",
        className
      )}
      {...props}
    />
  );
}

export {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupText,
  InputGroupInput,
  InputGroupTextarea,
};
